import React from "react";
import { useSelector } from "react-redux";

// Images
import excavatorImg from '../../assest/heavy.jpg';
import bulldozerImg from '../../assest/heavyone.jpg';
import craneImg from '../../assest/crane.jpg';
import FaqSection from "../../components/about/FAQ";
import ServicesCard from "../../components/home/ServicesCard";

const translations = {
    gb: {
        heading: "Heavy Equipment",
        buttonText: "Explore Services",
        introText: "Our range of heavy equipment is built to perform in the most demanding environments. Whether you're working on a construction project, excavation job, or industrial operation, our machines are ready to deliver unmatched performance and reliability.",
        categoriesTitle: "Our Equipment Categories",
        featuredTitle: "Featured Equipment",
        featuredDescription: "Heavy equipment refers to large machines designed to perform construction, excavation, and other heavy-duty tasks.",
        whyChooseTitle: "Why Choose Our Equipment?",
        specsLabel: "Specs:",
        moreDetail: "More Detail",
        names: {
            Excavator: "Excavator",
            Bulldozer: "Bulldozer",
            Crane: "Crane"
        }
    },
    sa: {
        heading: "المعدات الثقيلة",
        buttonText: "استكشف الخدمات",
        introText: "مجموعتنا من المعدات الثقيلة مصممة لأداء في أكثر البيئات تطلبًا. سواء كنت تعمل في مشروع بناء أو حفر أو تشغيل صناعي، فإن آلاتنا جاهزة لتقديم أداء وموثوقية لا مثيل لهما.",
        categoriesTitle: "فئات معداتنا",
        featuredTitle: "المعدات المميزة",
        featuredDescription: "تشير المعدات الثقيلة إلى الآلات الكبيرة المصممة لأداء أعمال البناء والحفر والمهام الأخرى ذات المهام الشاقة.",
        whyChooseTitle: "لماذا تختار معداتنا؟",
        specsLabel: "المواصفات:",
        moreDetail: "المزيد من التفاصيل",
        names: {
            Excavator: "حفارة",
            Bulldozer: "جرافة",
            Crane: "رافعة"
        }
    },
    fr: {
        heading: "Équipement Lourd",
        buttonText: "Explorer les services",
        introText: "Notre gamme d'équipements lourds est conçue pour performer dans les environnements les plus exigeants. Que vous travailliez sur un projet de construction, d'excavation ou d'exploitation industrielle, nos machines sont prêtes à offrir des performances et une fiabilité inégalées.",
        categoriesTitle: "Nos catégories d'équipement",
        featuredTitle: "Équipement en vedette",
        featuredDescription: "Les équipements lourds désignent les grandes machines conçues pour effectuer des travaux de construction, d'excavation et d'autres tâches lourdes.",
        whyChooseTitle: "Pourquoi choisir notre équipement?",
        specsLabel: "Spécifications :",
        moreDetail: "Plus de détails",
        names: {
            Excavator: "Excavatrice",
            Bulldozer: "Bulldozer",
            Crane: "Grue"
        }
    },
    de: {
        heading: "Schwere Ausrüstung",
        buttonText: "Dienstleistungen erkunden",
        introText: "Unsere Palette an schweren Geräten ist für den Einsatz in den anspruchsvollsten Umgebungen konzipiert. Egal, ob Sie an einem Bauprojekt, einer Ausgrabung oder einem Industriebetrieb arbeiten, unsere Maschinen sind bereit, unübertroffene Leistung und Zuverlässigkeit zu liefern.",
        categoriesTitle: "Unsere Ausrüstungskategorien",
        featuredTitle: "Vorgestellte Ausrüstung",
        featuredDescription: "Schwere Ausrüstung bezieht sich auf große Maschinen, die für Bauarbeiten, Ausgrabungen und andere schwere Aufgaben konzipiert sind.",
        whyChooseTitle: "Warum unsere Ausrüstung wählen?",
        specsLabel: "Spezifikationen:",
        moreDetail: "Mehr Details",
        names: {
            Excavator: "Bagger",
            Bulldozer: "Planierraupe",
            Crane: "Kran"
        }
    }
};

const equipmentList = [
    {
        key: "Excavator",
        image: excavatorImg,
        specs: {
            gb: "Ideal for digging, trenching, and material handling on construction sites.",
            sa: "مثالي للحفر، إنشاء الخنادق، ونقل المواد في مواقع البناء.",
            fr: "Idéal pour creuser, creuser des tranchées et manipuler des matériaux sur les chantiers.",
            de: "Ideal zum Graben, Ausschachten und Materialhandling auf Baustellen."
        },
        description: {
            gb: "Excavators are versatile machines used mainly for digging foundations, trenches, and lifting heavy materials.",
            sa: "الحفارات هي آلات متعددة الاستخدامات تُستخدم بشكل أساسي لحفر الأساسات والخنادق ورفع المواد الثقيلة.",
            fr: "Les excavatrices sont des machines polyvalentes utilisées principalement pour creuser des fondations, des tranchées et soulever des matériaux lourds.",
            de: "Bagger sind vielseitige Maschinen, die hauptsächlich zum Graben von Fundamenten, Gräben und zum Heben schwerer Materialien verwendet werden."
        },
        link: "/excavators"
    },
    {
        key: "Bulldozer",
        image: bulldozerImg,
        specs: {
            gb: "Heavy-duty machine for pushing large quantities of soil, sand, or debris.",
            sa: "آلة ثقيلة لدفع كميات كبيرة من التربة أو الرمل أو الحطام.",
            fr: "Machine robuste pour pousser de grandes quantités de terre, de sable ou de débris.",
            de: "Schwermaschine zum Schieben großer Mengen Erde, Sand oder Schutt."
        },
        description: {
            gb: "Bulldozers are powerful tracked vehicles equipped with a large blade, perfect for land clearing and grading.",
            sa: "الجرافات هي مركبات مجنزرة قوية مزودة بشفرة كبيرة، مثالية لتسوية الأرض وإزاحة العقبات.",
            fr: "Les bulldozers sont des véhicules puissants sur chenilles équipés d'une grande lame, parfaits pour le nivellement du terrain.",
            de: "Planierraupen sind leistungsstarke Kettenfahrzeuge mit einer großen Schaufel – ideal zum Einebnen von Gelände."
        },
        link: "/bulldozers"
    },
    {
        key: "Crane",
        image: craneImg,
        specs: {
            gb: "Perfect for lifting and moving heavy materials at high elevations.",
            sa: "مثالية لرفع ونقل المواد الثقيلة على ارتفاعات عالية.",
            fr: "Parfait pour soulever et déplacer des matériaux lourds en hauteur.",
            de: "Ideal zum Heben und Bewegen schwerer Materialien in großer Höhe."
        },
        description: {
            gb: "Cranes help in lifting heavy loads vertically and horizontally on construction and industrial sites.",
            sa: "الرافعات تساعد في رفع الأحمال الثقيلة رأسياً وأفقياً في مواقع البناء والصناعة.",
            fr: "Les grues permettent de soulever des charges lourdes verticalement et horizontalement sur les chantiers et les sites industriels.",
            de: "Krane helfen beim vertikalen und horizontalen Heben schwerer Lasten auf Baustellen und in der Industrie."
        },
        link: "/excavators"
    }
];

const imageStyle = {
    height: '250px',
    width: '100%',
    objectFit: 'cover',
    borderRadius: '8px',
};

const HeavyEquipment = () => {
    const { language } = useSelector((state) => state.language);
    const t = translations[language] || translations.gb;

    return (
        <div>
            {/* Introduction Text */}
            <section
                style={{
                    background: 'radial-gradient(circle, #ffffff 0%, #f8d4d4 100%)',
                    padding: '20px',
                }}
            >
                <h1 className="text-center mb-4 mt-5">{t.heading}</h1>
                <p
                    className="text-center mb-5 mx-auto"
                    style={{ fontSize: '1.1rem', maxWidth: '70%' }}
                >
                    {t.introText}
                </p>
            </section>

            {/* Categories */}
            <section className="container py-5">
                <h2 className="text-center mb-4">{t.categoriesTitle}</h2>
                <div className="row text-center">
                    {equipmentList.map((item, idx) => (
                        <div className="col-md-4 mb-4" key={idx}>
                            <img src={item.image} alt={item.key} style={imageStyle} />
                            <h5 className="mt-3">{t.names[item.key]}</h5>
                            <p><strong>{t.specsLabel}</strong> {item.specs[language]}</p>
                            <p>{item.description[language]}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Featured Equipment */}
            <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="container">
                    <h2 className="text-center mb-4">{t.featuredTitle}</h2>
                    <p className="text-center mb-4">{t.featuredDescription}</p>

                    <ul className="list-group">
                        {equipmentList.map((item, idx) => (
                            <li key={idx} className="list-group-item mb-3">
                                <div className="row">
                                    <div className="col-md-3">
                                        <img src={item.image} alt={item.key} style={imageStyle} />
                                    </div>
                                    <div className="col-md-9">
                                        <h5>{t.names[item.key]}</h5>
                                        <p>{item.description[language]}</p>
                                        <p><strong>{t.specsLabel}</strong> {item.specs[language]}</p>
                                        <div className="mt-3" >
                                            <a href={item.link} className="btn btn-outline-primary" style={{ backgroundColor: 'red', border: '0', color: 'white' }}>
                                                {t.moreDetail}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <ServicesCard showHeading={false} showParagraph={false} />
            <FaqSection />
        </div>
    );
};

export default HeavyEquipment;
